import { Avatar, AvatarGroup, Badge, Box, Button, Grow, Icon, Stack, Toolbar, Tooltip, Typography, alpha } from "@mui/material";
import MyToastShow from '@/components/Layout/MyToast';
import { useMemo } from "react";
import { IUser } from "@/redux/models";
import { useTablaCarpetaContext } from '../../logics/TablaCarritoProvider';
import { modalCarpetaType } from '../Modals';
import FilterHeaderMenu from './FilterHeaderMenu';
import { ViewAs } from "../../logics/useTablaCarritoV2";
import TableCarpetaPagination from "./TableCarpetaPagination";

const TableCarpetasToolbar = () => {
    const { context } = useTablaCarpetaContext();
    const numSelected = context.selectedCarritos.length;
    const allowMultipleUsers = context.isForEmpresa

    const usuarios = useMemo(() => {
        const users = {} as { [key: number]: IUser & { cantCarpetas: number } };
        context.selectedCarritos.forEach((carpeta) => {
            if (users.hasOwnProperty(carpeta.usuario.id)) {
                users[carpeta.usuario.id] = { ...users[carpeta.usuario.id], cantCarpetas: users[carpeta.usuario.id].cantCarpetas + 1 }
            } else {
                users[carpeta.usuario.id] = { ...carpeta.usuario, cantCarpetas: 1 }
            }
        });
        return users;

    }, [context.selectedCarritos]);

    const actionButtons = useMemo(() => (
        <Stack direction="row" spacing={2}>

            <Grow in={numSelected > 1}>

                <Button
                    variant="outlined"
                    startIcon={<Icon>groups</Icon>}
                    sx={{
                        display: context.selectedCarritos.some(c => c.usuario.id !== context.selectedCarritos[0].usuario.id) ?
                            { opacity: 0.01, backgroundColor: 'grey.300', color: 'grey.600', cursor: 'not-allowed' }
                            :
                            {}
                    }}

                    onClick={() => {
                        if (context.selectedCarritos.some(c => c.usuario.id !== context.selectedCarritos[0].usuario.id)) {
                            return MyToastShow({ message: 'No se pueden agrupar carpetas de distintos usuarios', type: 'error' })
                        }
                        context.setModal(modalCarpetaType.AGRUPAR_CARPETAS)
                    }}
                >
                    Agrupar
                </Button>
            </Grow>
            <Grow in={numSelected >= 1}>
                <Button
                    variant="outlined"
                    startIcon={<Icon>point_of_sale</Icon>}
                    onClick={() => {
                        if (!allowMultipleUsers && context.selectedCarritos.some(c => c.usuario.id !== context.selectedCarritos[0].usuario.id)) {
                            return MyToastShow({ message: 'No se pueden liquidar carpetas de distintos usuarios', type: 'error' })
                        }
                        context.setModal(modalCarpetaType.LIQUIDAR_CARPETAS)
                    }}
                >
                    Liquidar
                </Button>
            </Grow>
            <Button
                variant="outlined"
                startIcon={<Icon>payments</Icon>}
                onClick={() => context.setModal(modalCarpetaType.PAGAR_CARPETAS)}
            >
                Pagar
            </Button>

        </Stack >
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [numSelected, context.modal]);

    return (
        <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(numSelected > 0 && {
                    bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                }),
            }}
        >
            {numSelected > 0 ? (
                <Box sx={{ flex: '1 1 100%', columnGap: '2rem', display: 'flex' }}>
                    <Typography
                        color="inherit"
                        variant="subtitle1"
                        component="div"
                        alignSelf='center'
                    >
                        {numSelected} carpetas seleccionadas
                    </Typography>
                    <AvatarGroup
                        total={usuarios && Object.values(usuarios).length}
                        max={5}
                        sx={{
                            '& .MuiAvatar-root': { width: 40, height: 40, fontSize: 17 },
                        }}
                    >
                        {usuarios &&
                            Object.values(usuarios).map((user) => (
                                <Tooltip key={user.id} title={user.nombre + ' ' + user.apellido}>
                                    <Badge
                                        overlap="circular"
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        badgeContent={
                                            <Box
                                                sx={{
                                                    width: 22,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    aspectRatio: '1/1',
                                                    justifyContent: 'center',
                                                    backgroundColor: 'primary.main',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    fontSize: '14px',
                                                    '&:hover': {
                                                        transform: 'scale(1.2)',
                                                        transition: 'transform 0.3s'
                                                    }
                                                }}
                                            >
                                                {user.cantCarpetas}
                                            </Box>
                                        }
                                    >
                                        <Avatar
                                            sx={{ width: 40, height: 40, cursor: 'pointer' }}
                                            alt={user.nombre + ' ' + user.apellido}
                                            src={user.fotoPerfil}
                                            variant="circular"
                                            onClick={() => {
                                                context.setSelectedCarritos(context.selectedCarritos.filter(c => c.usuario.id !== user.id))
                                            }}
                                        />
                                    </Badge>
                                </Tooltip>
                            ))}
                    </AvatarGroup>
                </Box>
            ) : (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    variant="h6"
                    id="tableTitle"
                    component="div"
                >
                    Carpetas
                </Typography>
            )}
            <Box sx={{ flex: '1 1 100%' }} >
                <TableCarpetaPagination />
            </Box>

            {numSelected > 0 && actionButtons}
            {context.viewAs === ViewAs.ADMIN && <FilterHeaderMenu />}

        </Toolbar>
    );
}

export default TableCarpetasToolbar;
